// src/components/suppliers/FormAddEditSupplier.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  useTheme,
  Divider,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import { useTranslation } from "react-i18next";

import useCreateSupplier from "../../hooks/suppliers/useCreateSupplier";
import useUpdateSupplier from "../../hooks/suppliers/useUpdateSupplier";
import { useUploadMedia } from "../../hooks/media/useUploadMedia";
import useGetServicesForSelect from "../../hooks/services/useGetServicesForSelect";
import useGetPaxTypesForSelect from "../../hooks/paxTypes/useGetPaxTypesForSelect";

import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import ServicesPricingSection from "./ServicesPricingSection";
import i18next from "i18next";
import toast from "react-hot-toast";
import { countries } from "../../constants/countres";
import {
  formatFullPhoneNumber,
  validatePhoneNumber,
  parseFullPhoneNumber,
} from "../../utils/phoneUtils";

import useSingleImageField from "../../hooks/media/useSingleImageField";

const FormAddEditSupplier = ({ currentData, isEdit = false, onSubmitSuccess }) => {
  const { i18n } = useTranslation();
  const lng = i18n.language || "en";
  const isArabic = lng === "ar";
  const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

  const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
  const showArabic = activeLang === "ar";
  const showEnglish = activeLang === "en";

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Basic Fields
  const [nameAr, setNameAr] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [addressAr, setAddressAr] = useState("");
  const [addressEn, setAddressEn] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Phone country code
  const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].phone);

  // Services with pricing
  const [selectedServices, setSelectedServices] = useState([]);
  const [servicesPricing, setServicesPricing] = useState([]);

  const { createSupplierMutate, isPending: isCreating } = useCreateSupplier();
  const { updateSupplierMutate, isPending: isUpdating } = useUpdateSupplier();
  const { uploadMedia, isPending: isUploading } = useUploadMedia();
  const { options: servicesOptions, isLoading: isLoadingServices } = useGetServicesForSelect();
  const { options: paxTypesOptions, isLoading: isLoadingPaxTypes } = useGetPaxTypesForSelect();

  const loadingSubmit = isCreating || isUpdating;

  // ✅ Image logic extracted to hook
  const img = useSingleImageField({
    initialImage: null, // سنزامنها عند edit
    uploadMedia,
    isArabic,
  });

  useEffect(() => {
    if (isEdit && currentData) {
      // ✅ فصل رقم الهاتف عند التعديل أولاً
      if (currentData?.phone) {
        const { countryPhone, nationalNumber } = parseFullPhoneNumber(currentData.phone);
        setSelectedCountryCode(countryPhone);
        setPhone(nationalNumber);
      }

      setNameAr(currentData?.name?.ar || "");
      setNameEn(currentData?.name?.en || "");
      setAddressAr(currentData?.address?.ar || "");
      setAddressEn(currentData?.address?.en || "");
      setWebsite(currentData?.website || "");
      setEmail(currentData?.email || "");

      // ✅ تزامن صورة المورد من السيرفر (ويسجل id عشان زر الحذف يشتغل)
      img.setFromServerImage(currentData?.image || null);

      // Load services with pricing
      if (currentData?.services && Array.isArray(currentData.services)) {
        const serviceIds = currentData.services.map((s) => String(s.id));
        setSelectedServices(serviceIds);

        const pricing = currentData.services.map((s) => ({
          service_id: s.id,
          currency_id: s.pivot?.currency_id || s.currency_id || 1,
          pax_type_prices: s.pax_type_prices?.map((p) => ({
            pax_type_id: p.pax_type_id,
            price: p.price || 0,
            cost_price: p.cost_price || 0,
          })) || [],
        }));
        setServicesPricing(pricing);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentData]);

  // ✅ Sync pax types into pricing rows after they load (fixes race condition in edit mode)
  useEffect(() => {
    if (!paxTypesOptions || paxTypesOptions.length === 0) return;
    if (selectedServices.length === 0) return;

    setServicesPricing(prev =>
      prev.map(item => {
        const syncedPaxPrices = paxTypesOptions.map(pt => {
          const existing = item.pax_type_prices?.find(
            p => String(p.pax_type_id) === String(pt.value)
          );
          return existing || { pax_type_id: pt.value, price: 0, cost_price: 0 };
        });
        return { ...item, pax_type_prices: syncedPaxPrices };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paxTypesOptions]);

  const handleServicesChange = (event) => {
    const newSelectedIds = event.target.value;
    setSelectedServices(newSelectedIds);

    const updatedPricing = newSelectedIds.map((serviceId) => {
      const existing = servicesPricing.find((p) => String(p.service_id) === String(serviceId));
      if (existing) {
        // Ensure it has all pax types if any are missing
        const syncedPaxPrices = paxTypesOptions?.map(pt => {
          const existingPax = existing.pax_type_prices?.find(p => String(p.pax_type_id) === String(pt.value));
          return existingPax || { pax_type_id: pt.value, price: 0, cost_price: 0 };
        }) || [];
        return { ...existing, pax_type_prices: syncedPaxPrices };
      }

      return {
        service_id: parseInt(serviceId),
        currency_id: 1,
        pax_type_prices: paxTypesOptions?.map(pt => ({
          pax_type_id: pt.value,
          price: 0,
          cost_price: 0
        })) || []
      };
    });
    setServicesPricing(updatedPricing);
  };

  const handlePricingChange = (serviceId, paxTypeId, field, value) => {
    setServicesPricing((prev) =>
      prev.map((item) => {
        if (String(item.service_id) === String(serviceId)) {
          const updatedPaxPrices = item.pax_type_prices.map(pax => {
            if (String(pax.pax_type_id) === String(paxTypeId)) {
              return { ...pax, [field]: parseFloat(value) || 0 };
            }
            return pax;
          });
          return { ...item, pax_type_prices: updatedPaxPrices };
        }
        return item;
      })
    );
  };

  const handleRemoveService = (serviceId) => {
    setSelectedServices((prev) => prev.filter((id) => String(id) !== String(serviceId)));
    setServicesPricing((prev) => prev.filter((item) => String(item.service_id) !== String(serviceId)));
  };

  const getServiceName = (serviceId) => {
    const service = servicesOptions?.find((s) => String(s.value) === String(serviceId));
    return service?.label || `Service ${serviceId}`;
  };

  // التحقق من صحة جميع الأسعار قبل الإرسال
  const validateAllPricing = () => {
    for (const service of servicesPricing) {
      if (!service.pax_type_prices || service.pax_type_prices.length === 0) continue;

      for (const pricing of service.pax_type_prices) {
        const costPrice = parseFloat(pricing.cost_price) || 0;
        const sellPrice = parseFloat(pricing.price) || 0;

        if (costPrice > 0 && sellPrice > 0 && costPrice > sellPrice) {
          return {
            isValid: false,
            message: isArabic
              ? `سعر التكلفة أعلى من سعر البيع لإحدى أنواع الركاب في الخدمة: ${getServiceName(service.service_id)}`
              : `Cost price is higher than sell price for one of the pax types in: ${getServiceName(service.service_id)}`,
          };
        }
      }
    }
    return { isValid: true };
  };

  const SubmitForm = (e) => {
    e.preventDefault();

    // ✅ التحقق من صحة رقم الهاتف
    if (phone) {
      const phoneValidation = validatePhoneNumber(selectedCountryCode, phone, lng);
      if (!phoneValidation.isValid) {
        toast.error(phoneValidation.message);
        return;
      }
    }

    // ✅ تكوين الرقم الكامل
    const fullPhone = phone ? formatFullPhoneNumber(selectedCountryCode, phone) : null;

    // التحقق من الأسعار
    const validation = validateAllPricing();
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    const payload = {
      name: {
        ar: nameAr || nameEn,
        en: nameEn || nameAr,
      },
      address: {
        ar: addressAr || addressEn,
        en: addressEn || addressAr,
      },
      website: website || null,
      phone: fullPhone,
      email: email || null,

      // ✅ لا تبعت image key لو مفيش صورة
      ...img.getImagePayload(),

      services: servicesPricing,
    };

    if (isEdit && currentData?.id) {
      updateSupplierMutate(
        { req: payload, id: currentData.id },
        { onSuccess: () => onSubmitSuccess?.() }
      );
    } else {
      createSupplierMutate(payload, {
        onSuccess: () => onSubmitSuccess?.(),
      });
    }
  };

  return (
    <Paper
      sx={{
        p: 3,
        mt: 2,
        direction: isArabic ? "rtl" : "ltr",
        backgroundColor: isDark ? "transparent" : "#FFFFFF",
      }}
    >
      <Box
        component="form"
        onSubmit={SubmitForm}
        sx={{
          p: { xs: 2, md: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon color="primary" />
            {isEdit
              ? isArabic
                ? "تعديل مورد"
                : "Edit Supplier"
              : isArabic
                ? "إضافة مورد جديد"
                : "Create New Supplier"}
          </Typography>

          <LanguageToggle
            activeLang={activeLang}
            onChange={(lng) => setActiveLang(lng)}
            showLabel={true}
          />
        </Box>

        {/* Image Upload Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FormInput
            required={false}
            type="file"
            name="image"
            label={isArabic ? "شعار المورد" : "Supplier Logo"}
            labelImage={isArabic ? "اختر الصورة" : "Choose image"}
            labelBioImage={
              isArabic
                ? "اسحب وأسقط الصورة هنا أو انقر للاختيار"
                : "Drag & drop or click to upload"
            }
            value={img.imagePreview}
            onChange={img.handleImageChange}
            isUploading={isUploading}
            errorMessage={img.imageError}
            successMessage={
              img.imageName && !img.imageError
                ? isArabic
                  ? "تم رفع الصورة بنجاح"
                  : "Image uploaded successfully"
                : ""
            }
            inputProps={{ accept: "image/*" }}

            // ✅ لتفعيل الحذف من السيرفر في edit
            mediaId={img.imageId}

            // ✅ بعد الحذف (سيرفر/محلي): صفّر الحالة (وبالتالي لن يُرسل image)
            onDeleted={img.handleDeleted}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Basic Information */}
        <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <BusinessIcon fontSize="small" color="primary" />
          {isArabic ? "المعلومات الأساسية" : "Basic Information"}
        </Typography>

        <Grid container spacing={2}>
          {showArabic && (
            <>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="الاسم (عربي)"
                  placeholder="أدخل اسم المورد بالعربي"
                  type="text"
                  name="name_ar"
                  value={nameAr}
                  onChange={(e) => setNameAr(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="العنوان (عربي)"
                  placeholder="أدخل عنوان المورد بالعربي"
                  type="text"
                  name="address_ar"
                  value={addressAr}
                  onChange={(e) => setAddressAr(e.target.value)}
                  required={false}
                />
              </Grid>
            </>
          )}

          {showEnglish && (
            <>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Name (English)"
                  placeholder="Enter supplier name in English"
                  type="text"
                  name="name_en"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Address (English)"
                  placeholder="Enter supplier address in English"
                  type="text"
                  name="address_en"
                  value={addressEn}
                  onChange={(e) => setAddressEn(e.target.value)}
                  required={false}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={4}>
            <FormInput
              label={isArabic ? "الموقع الإلكتروني" : "Website"}
              placeholder="https://example.com"
              type="url"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              required={false}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormInput
              label={isArabic ? "رقم الهاتف" : "Phone Number"}
              placeholder={isArabic ? "+20123456789" : "+20123456789"}
              type="tel"
              name="phone"
              phoneNumber={phone}
              setPhoneNumber={setPhone}
              selectedCountryCode={selectedCountryCode}
              setSelectedCountryCode={setSelectedCountryCode}
              fullPhoneNumber={isEdit ? currentData?.phone : null}
              required={false}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormInput
              label={isArabic ? "البريد الإلكتروني" : "Email"}
              placeholder="info@example.com"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required={false}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Services Pricing Section */}
        <ServicesPricingSection
          isDark={isDark}
          isLoadingServices={isLoadingServices}
          servicesOptions={servicesOptions}
          selectedServices={selectedServices}
          handleServicesChange={handleServicesChange}
          servicesPricing={servicesPricing}
          handlePricingChange={handlePricingChange}
          handleRemoveService={handleRemoveService}
          getServiceName={getServiceName}
          paxTypesOptions={paxTypesOptions}
          isLoadingPaxTypes={isLoadingPaxTypes}
        />

        {/* Submit Button */}
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="primary"
            disabled={
              loadingSubmit || isUploading || selectedServices.length === 0 || isLoadingServices
            }
            startIcon={!loadingSubmit && <AddCircleOutlineIcon />}
            sx={{
              minWidth: 150,
              fontWeight: 600,
              boxShadow: 3,
              "&:hover": { boxShadow: 6 },
            }}
          >
            {loadingSubmit
              ? isArabic
                ? "⏳ جارٍ الحفظ..."
                : "⏳ Saving..."
              : isEdit
                ? isArabic
                  ? " تحديث"
                  : " Update"
                : isArabic
                  ? " حفظ"
                  : " Save"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default FormAddEditSupplier;
